/**
 * Approver-facing review of employee profile submissions.
 *
 * Company Admin and Owner only — the route guard in App.tsx enforces it, and
 * the API enforces it again. This is the one screen that shows an employee's
 * full bank account, PAN and Aadhaar, because approving a submission is the
 * authorised reason to see them; opening a record is audited server-side.
 *
 * A queue on the left, one record's full detail on the right. The reviewer
 * reads the details and the uploaded documents, then approves — which locks
 * the record — or rejects with a reason that sends it back to the employee to
 * re-enter. An approved record can later be unlocked, with a reason, for a
 * genuine change such as a bank account that really moved.
 */
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CheckCircle2,
  Clock,
  FileText,
  Loader2,
  Lock,
  ShieldCheck,
  Unlock,
} from 'lucide-react';
import { toast } from 'sonner';
import { client, errorMessage } from '../../api/client';
import type {
  Page,
  ProfileReviewDetail,
  ProfileReviewQueueItem,
  ProfileReviewStatus,
  StoredDocument,
} from '../../types';

const STATUS_TABS: { key: ProfileReviewStatus; label: string }[] = [
  { key: 'pending', label: 'Pending' },
  { key: 'approved', label: 'Approved' },
  { key: 'rejected', label: 'Sent back' },
];

const DOC_LABELS: Record<string, string> = {
  profile_photo: 'Profile photo',
  aadhaar_front: 'Aadhaar — front',
  aadhaar_back: 'Aadhaar — back',
  pan_card: 'PAN card',
  education_certificate: 'Certificate scan',
};

function StatusPill({ status }: { status: ProfileReviewStatus }) {
  const map: Record<ProfileReviewStatus, string> = {
    draft: 'bg-gray-100 text-gray-600',
    pending: 'bg-amber-100 text-amber-800',
    approved: 'bg-emerald-100 text-emerald-800',
    rejected: 'bg-rose-100 text-rose-800',
  };
  const label = status === 'rejected' ? 'sent back' : status;
  return (
    <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium capitalize ${map[status]}`}>
      {label}
    </span>
  );
}

function Row({ label, value, mono }: { label: string; value: string | null | undefined; mono?: boolean }) {
  return (
    <div className="flex gap-3 py-1.5">
      <dt className="w-40 shrink-0 text-xs text-gray-500">{label}</dt>
      <dd className={`min-w-0 flex-1 break-words text-sm text-gray-900 ${mono ? 'font-mono' : ''}`}>
        {value || <span className="text-gray-300">—</span>}
      </dd>
    </div>
  );
}

/**
 * One uploaded document. Images render inline so the reviewer can actually
 * read an Aadhaar card; anything else (a PDF) is a download. The blob is
 * fetched through the same audited /download route the rest of the app uses.
 */
function DocumentTile({ doc }: { doc: StoredDocument }) {
  const [url, setUrl] = useState<string | null>(null);
  const isImage = doc.contentType.startsWith('image/');

  useEffect(() => {
    if (!isImage) return;
    let stale = false;
    let objectUrl: string | null = null;
    void client
      .get(`/api/v1/documents/${doc.id}/download`, { responseType: 'blob' })
      .then((res) => {
        if (stale) return;
        objectUrl = URL.createObjectURL(res.data as Blob);
        setUrl(objectUrl);
      })
      .catch(() => undefined);
    return () => {
      stale = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [doc.id, isImage]);

  const download = async () => {
    try {
      const res = await client.get(`/api/v1/documents/${doc.id}/download`, {
        responseType: 'blob',
      });
      const objectUrl = URL.createObjectURL(res.data as Blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = doc.filename;
      link.click();
      URL.revokeObjectURL(objectUrl);
    } catch (e) {
      toast.error(errorMessage(e, 'Could not download that file'));
    }
  };

  return (
    <div className="rounded-lg p-2 ring-1 ring-gray-200">
      <p className="mb-1 text-xs font-medium text-gray-700">
        {DOC_LABELS[doc.docType] ?? doc.docType}
      </p>
      {isImage ? (
        url ? (
          <button type="button" onClick={() => void download()} className="block w-full">
            <img
              src={url}
              alt={DOC_LABELS[doc.docType] ?? doc.docType}
              className="max-h-56 w-full rounded object-contain ring-1 ring-gray-100"
            />
          </button>
        ) : (
          <div className="flex h-32 items-center justify-center text-gray-300">
            <Loader2 className="h-4 w-4 animate-spin" />
          </div>
        )
      ) : (
        <button
          type="button"
          className="btn-ghost text-xs"
          onClick={() => void download()}
        >
          <FileText className="h-3.5 w-3.5" />
          {doc.filename}
        </button>
      )}
    </div>
  );
}

export default function ProfileReviewsPage() {
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<ProfileReviewStatus>('pending');
  const [selected, setSelected] = useState<string | null>(null);
  const [reason, setReason] = useState('');

  const queue = useQuery<Page<ProfileReviewQueueItem>>({
    queryKey: ['profile-reviews', tab],
    queryFn: async () =>
      (await client.get('/api/v1/profile-reviews', { params: { status: tab } })).data,
  });

  const detail = useQuery<ProfileReviewDetail>({
    queryKey: ['profile-review', selected],
    queryFn: async () =>
      (await client.get(`/api/v1/profile-reviews/${selected}`)).data,
    enabled: Boolean(selected),
  });

  // Clear the reason box whenever a different record is opened, so a note typed
  // against one submission cannot be sent against the next.
  useEffect(() => setReason(''), [selected]);

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['profile-reviews'] });
    void queryClient.invalidateQueries({ queryKey: ['profile-review', selected] });
  };

  const decide = (action: 'approve' | 'reject' | 'unlock') =>
    client
      .post(`/api/v1/profile-reviews/${selected}/${action}`, { note: reason.trim() || null })
      .then(() => {
        invalidate();
        setReason('');
      });

  const approve = useMutation({
    mutationFn: () => decide('approve'),
    onSuccess: () => toast.success('Profile approved and locked'),
    onError: (e) => toast.error(errorMessage(e, 'Could not approve')),
  });
  const reject = useMutation({
    mutationFn: () => {
      if (!reason.trim()) {
        return Promise.reject(new Error('A reason is required to send a profile back'));
      }
      return decide('reject');
    },
    onSuccess: () => toast.success('Sent back to the employee'),
    onError: (e) => toast.error(errorMessage(e, 'Could not send back')),
  });
  const unlock = useMutation({
    mutationFn: () => {
      if (!reason.trim()) {
        return Promise.reject(new Error('A reason is required to unlock a profile'));
      }
      return decide('unlock');
    },
    onSuccess: () => toast.success('Profile unlocked for changes'),
    onError: (e) => toast.error(errorMessage(e, 'Could not unlock')),
  });

  const busy = approve.isPending || reject.isPending || unlock.isPending;
  const items = queue.data?.items ?? [];

  return (
    <div className="space-y-4">
      <div>
        <h1 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
          <ShieldCheck className="h-5 w-5 text-indigo-600" />
          Profile reviews
        </h1>
        <p className="mt-0.5 text-sm text-gray-500">
          Review what employees submit — details, bank, statutory IDs and
          documents. Approving locks the record; sending it back returns it with
          your reason. Opening a record is recorded in the audit trail.
        </p>
      </div>

      <div className="flex gap-1 border-b border-gray-200">
        {STATUS_TABS.map(({ key, label }) => (
          <button
            key={key}
            type="button"
            onClick={() => setTab(key)}
            className={`-mb-px border-b-2 px-3 py-2 text-sm ${
              tab === key
                ? 'border-indigo-600 font-medium text-indigo-700'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="grid gap-4 lg:grid-cols-[20rem,1fr]">
        {/* Queue */}
        <div className="space-y-2">
          {queue.isLoading ? (
            <div className="flex justify-center py-8 text-gray-400">
              <Loader2 className="h-5 w-5 animate-spin" />
            </div>
          ) : items.length === 0 ? (
            <p className="card text-sm text-gray-400">Nothing here.</p>
          ) : (
            items.map((item) => (
              <button
                key={item.employeeId}
                type="button"
                onClick={() => setSelected(item.employeeId)}
                className={`card w-full text-left transition ${
                  selected === item.employeeId ? 'ring-2 ring-indigo-400' : 'hover:bg-gray-50'
                }`}
              >
                <div className="flex items-center justify-between gap-2">
                  <p className="truncate text-sm font-medium text-gray-900">
                    {item.fullName ?? item.employeeCode}
                  </p>
                  <StatusPill status={item.profileStatus} />
                </div>
                <p className="mt-0.5 truncate text-xs text-gray-500">
                  {item.designation ?? item.employeeCode}
                </p>
                {item.profileSubmittedAt && (
                  <p className="mt-1 flex items-center gap-1 text-[11px] text-gray-400">
                    <Clock className="h-3 w-3" />
                    Submitted {item.profileSubmittedAt.slice(0, 10)}
                  </p>
                )}
              </button>
            ))
          )}
        </div>

        {/* Detail */}
        <div>
          {!selected ? (
            <div className="card flex h-full items-center justify-center py-16 text-sm text-gray-400">
              Select a submission to review.
            </div>
          ) : detail.isLoading || !detail.data ? (
            <div className="card flex justify-center py-16 text-gray-400">
              <Loader2 className="h-5 w-5 animate-spin" />
            </div>
          ) : (
            <ReviewDetail
              d={detail.data}
              reason={reason}
              setReason={setReason}
              busy={busy}
              onApprove={() => approve.mutate()}
              onReject={() => reject.mutate()}
              onUnlock={() => unlock.mutate()}
            />
          )}
        </div>
      </div>
    </div>
  );
}

function ReviewDetail({
  d,
  reason,
  setReason,
  busy,
  onApprove,
  onReject,
  onUnlock,
}: {
  d: ProfileReviewDetail;
  reason: string;
  setReason: (v: string) => void;
  busy: boolean;
  onApprove: () => void;
  onReject: () => void;
  onUnlock: () => void;
}) {
  const pending = d.profileStatus === 'pending';
  const approved = d.profileStatus === 'approved';

  return (
    <div className="space-y-4">
      <section className="card">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <h2 className="text-base font-semibold text-gray-900">{d.fullName ?? d.employeeCode}</h2>
            <p className="mt-0.5 text-xs text-gray-500">
              {[d.designation, d.department, d.employeeCode].filter(Boolean).join(' · ')}
            </p>
          </div>
          <StatusPill status={d.profileStatus} />
        </div>
        {d.profileDecisionNote && (
          <p className="mt-2 rounded bg-gray-50 px-2.5 py-2 text-xs text-gray-600">
            <span className="font-medium">Last note:</span> {d.profileDecisionNote}
            {d.profileDecidedByName ? ` — ${d.profileDecidedByName}` : ''}
          </p>
        )}
      </section>

      <section className="card space-y-1">
        <h3 className="mb-1 text-sm font-semibold text-gray-900">Details</h3>
        <dl className="divide-y divide-gray-100">
          <Row label="Work email" value={d.email} />
          <Row label="Personal email" value={d.personalEmail} />
          <Row label="Date of birth" value={d.dateOfBirth} />
          <Row label="Blood group" value={d.bloodGroup} />
          <Row label="Emergency contact" value={d.emergencyContact} />
          <Row label="Address" value={d.address} />
        </dl>
      </section>

      <section className="card space-y-1">
        <h3 className="mb-1 flex items-center gap-1.5 text-sm font-semibold text-gray-900">
          <Lock className="h-4 w-4 text-indigo-600" />
          Bank &amp; statutory
        </h3>
        <dl className="divide-y divide-gray-100">
          <Row label="Bank name" value={d.bankName} />
          <Row label="IFSC" value={d.bankIfsc} mono />
          <Row label="Account number" value={d.bankAccountNumber} mono />
          <Row label="PAN" value={d.pan} mono />
          <Row label="Aadhaar" value={d.aadhaarNumber} mono />
          <Row label="UAN" value={d.uan} mono />
          <Row label="ESIC" value={d.esicNumber} mono />
        </dl>
      </section>

      <section className="card">
        <h3 className="mb-2 text-sm font-semibold text-gray-900">Documents</h3>
        {d.documents.length === 0 ? (
          <p className="text-xs text-gray-400">No documents uploaded.</p>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2">
            {d.documents.map((doc) => (
              <DocumentTile key={doc.id} doc={doc} />
            ))}
          </div>
        )}
      </section>

      {d.certificates.length > 0 && (
        <section className="card">
          <h3 className="mb-2 text-sm font-semibold text-gray-900">Certificates</h3>
          <ul className="space-y-1">
            {d.certificates.map((c) => (
              <li key={c.id} className="text-sm text-gray-700">
                {c.name}
                <span className="text-xs text-gray-400">
                  {c.issuingOrganisation ? ` · ${c.issuingOrganisation}` : ''}
                  {c.scans.length ? ` · ${c.scans.length} scan(s)` : ''}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Actions */}
      <section className="card space-y-3">
        {(pending || approved) && (
          <label className="block text-xs text-gray-600">
            {pending ? 'Reason (required to send back)' : 'Reason (required to unlock)'}
            <textarea
              className="input mt-1 h-20"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              maxLength={1000}
              placeholder={
                pending
                  ? 'What needs to be corrected before this can be approved?'
                  : 'Why is this record being reopened?'
              }
            />
          </label>
        )}

        <div className="flex flex-wrap gap-2">
          {pending && (
            <>
              <button type="button" className="btn-primary text-sm" disabled={busy} onClick={onApprove}>
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                Approve &amp; lock
              </button>
              <button type="button" className="btn-secondary text-sm" disabled={busy} onClick={onReject}>
                Send back
              </button>
            </>
          )}
          {approved && (
            <button type="button" className="btn-secondary text-sm" disabled={busy} onClick={onUnlock}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Unlock className="h-4 w-4" />}
              Unlock for changes
            </button>
          )}
          {d.profileStatus === 'rejected' && (
            <p className="text-xs text-gray-500">
              Sent back to the employee. Waiting for them to re-submit.
            </p>
          )}
        </div>
      </section>
    </div>
  );
}
